/**
 * Feature Flags client — fetches /api/feature-flags once on mount, caches in memory + localStorage.
 *
 * Usage:
 *   const promoEnabled = useFeatureFlag("show_promo_strip");
 *   if (!promoEnabled) return null;
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const FLAGS_CACHE_KEY = "ms_feature_flags_v1";
const FeatureFlagsContext = createContext({});

function readCache() {
    try {
        const raw = localStorage.getItem(FLAGS_CACHE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

function writeCache(data) {
    try { localStorage.setItem(FLAGS_CACHE_KEY, JSON.stringify(data)); } catch { /* ignore */ }
}

export function FeatureFlagsProvider({ children }) {
    const [flags, setFlags] = useState(() => readCache() || {});

    useEffect(() => {
        api.get("/feature-flags").then((r) => {
            setFlags(r.data || {});
            writeCache(r.data || {});
        }).catch(() => { /* keep cache */ });
    }, []);

    return <FeatureFlagsContext.Provider value={flags}>{children}</FeatureFlagsContext.Provider>;
}

export function useFeatureFlag(key, defaultValue = false) {
    const ctx = useContext(FeatureFlagsContext);
    if (!ctx || !(key in ctx)) return defaultValue;
    return !!ctx[key];
}

export function useAllFeatureFlags() {
    return useContext(FeatureFlagsContext);
}
